import customHeaders, { getSpace } from '../../common/headers';
import useGeneralStore from '../../store/general';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { ButtonWithIcon } from '../Buttons/ButtonWithIcon/ButtonWithIcon';
import chat_big_icon from './img/chat_big_icon.svg'
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

// Импортируем звуковой файл (добавьте файл notification.mp3 в папку с компонентом)
import notificationSoundFile from './audio/audio.mp3';

const NotificationChat = ({ children, setIsModalChat }) => {
    const selectedSpace = useGeneralStore(state => state.selectedSpace)
    const headers = { ...customHeaders, ...getSpace(selectedSpace) }

    // Используем useShallow правильно
    const {
        setChatListNotification,
        unreadCountCommon,
        setUnreadCountCommon,
        setUserStatus,
        unreadCountPersonal,
        setUnreadCountPersonal,
        unreadCountGroup,
        setUnreadCountGroup,
    } = useGeneralStore(
        useShallow((state) => ({
            unreadCountCommon: state.unreadCountCommon,
            setChatListNotification: state.setChatListNotification,
            setUnreadCountCommon: state.setUnreadCountCommon,
            setUserStatus: state.setUserStatus,
            unreadCountPersonal: state.unreadCountPersonal,
            setUnreadCountPersonal: state.setUnreadCountPersonal,
            unreadCountGroup: state.unreadCountGroup,
            setUnreadCountGroup: state.setUnreadCountGroup,
        }))
    );

    const [tokenAuth, setTokenAuth] = useState(null);
    const [isTokenLoading, setIsTokenLoading] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [error, setError] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        // Восстанавливаем настройку звука из localStorage
        const saved = localStorage.getItem('notificationSoundEnabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [notificationEnabled, setNotificationEnabled] = useState(() => {
        // Восстанавливаем настройку уведомлений из localStorage
        const saved = localStorage.getItem('notificationPermission');
        
        if (typeof Notification !== 'undefined') {
            return saved === 'granted' || Notification.permission === 'granted';
        }
        return saved === 'granted' || false;
    });

    // Используем глобальную переменную для хранения текущего сокета
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const audioRef = useRef(null);
    const navigate = useNavigate();

    // Флаг для предотвращения множественных переподключений
    const isMountedRef = useRef(true);

    // Флаг для отслеживания попытки подключения
    const isConnectionAttemptedRef = useRef(false);

    // Последнее сообщение для предотвращения дублирования уведомлений
    const lastNotificationTimeRef = useRef(0);
    const lastMessageIdRef = useRef(null);

    // Ref для отслеживания предыдущего space
    const prevSpaceRef = useRef(selectedSpace);

    // Инициализация аудио
    useEffect(() => {
        audioRef.current = new Audio(notificationSoundFile);
        audioRef.current.volume = 0.5;

        // Предзагрузка аудио
        audioRef.current.load();

        // Проверяем поддержку уведомлений
        if (!('Notification' in window)) {
            // console.log('Этот браузер не поддерживает уведомления');
        } else {
            // Проверяем текущий статус разрешения
            if (Notification.permission === 'granted') {
                // console.log('Уведомления уже разрешены');
            } else if (Notification.permission === 'denied') {
                // console.log('Уведомления заблокированы');
            }
        }

        return () => {
            isMountedRef.current = false;
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Сохранение настроек звука в localStorage
    useEffect(() => {
        localStorage.setItem('notificationSoundEnabled', JSON.stringify(soundEnabled));
    }, [soundEnabled]);

    // Сохранение статуса разрешения уведомлений
    useEffect(() => {
       
        if (typeof Notification !== 'undefined') {
            localStorage.setItem('notificationPermission', Notification.permission);
            setNotificationEnabled(Notification.permission === 'granted');
        } else {
            localStorage.setItem('notificationPermission', notificationEnabled ? 'granted' : 'denied');
        }
    }, [notificationEnabled]);

    // Функция для запроса разрешения на уведомления
    const requestNotificationPermission = useCallback(() => {
       
        if (typeof Notification === 'undefined') {
            alert('Уведомления не поддерживаются в этом браузере');
            return;
        }

        if (Notification.permission === 'granted') {
            setNotificationEnabled(true);
            return;
        }

        if (Notification.permission === 'denied') {
            alert('Вы заблокировали уведомления. Разрешите их в настройках браузера.');
            return;
        }

        // Запрашиваем разрешение
        Notification.requestPermission().then((permission) => {
            localStorage.setItem('notificationPermission', permission);
            setNotificationEnabled(permission === 'granted');

            if (permission === 'granted') {
                // console.log('Разрешение на уведомления получено');
                // Показываем тестовое уведомление
                showNotification('Уведомления включены', {
                    body: 'Вы будете получать уведомления о новых сообщениях',
                    tag: 'permission-granted'
                });
            }
        });
    }, []);

    // Функция воспроизведения звукового уведомления
    const playNotificationSound = useCallback(() => {
        // console.log('Attempting to play sound...');
        // console.log('Sound enabled:', soundEnabled);
        // console.log('Audio ref exists:', !!audioRef.current);

        if (!soundEnabled) {
            // console.log('Sound disabled by user');
            return;
        }

        if (!audioRef.current) {
            // console.log('Audio ref is null');
            return;
        }

        try {
            // console.log('Playing sound...');
            audioRef.current.currentTime = 0;

            // Попробуйте разные подходы
            const playPromise = audioRef.current.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        // console.log('Sound played successfully');
                    })
                    .catch(error => {
                        // console.error('Error playing sound:', error);
                        // console.log('Error name:', error.name);
                        // console.log('Error message:', error.message);

                        // Если ошибка autoplay, ждем взаимодействия пользователя
                        if (error.name === 'NotAllowedError') {
                            // console.log('Autoplay blocked. Sound will play on next user interaction');
                        }
                    });
            }
        } catch (error) {
            console.error('Exception in playNotificationSound:', error);
        }
    }, [soundEnabled]);

    // Функция для создания иконки из информации об авторе
    const createUserIconFromAuthor = useCallback((author) => {
        if (!author) return '/favicon.ico';

        try {
            // Получаем первую букву имени
            const firstLetter = author.name ? author.name.charAt(0).toUpperCase() : 'U';

            // Получаем цвет автора или используем цвет по умолчанию
            const bgColor = author.color || '#4a90e2';

            // Создаем canvas для генерации иконки
            const canvas = document.createElement('canvas');
            canvas.width = 128;
            canvas.height = 128;
            const ctx = canvas.getContext('2d');

            // Рисуем фон
            ctx.fillStyle = bgColor;
            ctx.beginPath();
            ctx.arc(64, 64, 64, 0, Math.PI * 2);
            ctx.fill();

            // Рисуем текст (первую букву имени)
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 64px Arial, sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(firstLetter, 64, 64);

            // Преобразуем canvas в data URL
            return canvas.toDataURL('image/png');
        } catch (error) {
            console.error('Error creating user icon:', error);
            return '/favicon.ico';
        }
    }, []);

    // Функция для показа уведомлений
    const showNotification = useCallback((title, options = {}) => {
      
        if (typeof Notification === 'undefined') {
            return null;
        }

        // Проверяем, активно ли окно
        if (document.hidden && notificationEnabled) {
            const notificationOptions = {
                icon: '/favicon.ico', // Укажите путь к иконке вашего приложения
                badge: '/favicon.ico',
                vibrate: [200, 100, 200],
                ...options
            };

            // Если передана иконка пользователя, добавляем ее в опции
            if (options.userIcon) {
                notificationOptions.icon = options.userIcon;
                notificationOptions.badge = options.userIcon;
            }

            // Форматируем body уведомления: делаем первую строку (имя автора) жирной
            if (options.body) {
                // Разделяем текст на строки
                const lines = options.body.split('\n');

                if (lines.length > 0) {
                    // Первую строку (имя автора) делаем жирной
                    // В Web Notifications нет поддержки HTML, поэтому используем символы для эмуляции
                    // Многие браузеры поддерживают базовое форматирование с помощью символов
                    const authorName = lines[0].trim();
                    const formattedBody = `**${authorName}**${lines.length > 1 ? '\n' + lines.slice(1).join('\n') : ''}`;
                    notificationOptions.body = formattedBody;
                }
            }

            try {
                const notification = new Notification(title, notificationOptions);

                // Обработка клика по уведомлению
                notification.onclick = () => {
                    window.focus();
                    notification.close();

                    // Навигация в чат
                    setIsModalChat(false);
                    window.scrollTo(0, 0);
                    navigate('/chat');
                };

                // Автоматическое закрытие уведомления через 5 секунд
                setTimeout(() => {
                    notification.close();
                }, 5000);

                return notification;
            } catch (error) {
                console.error('Ошибка при создании уведомления:', error);
                return null;
            }
        }
        return null;
    }, [notificationEnabled, navigate, setIsModalChat]);

    // Функция для очистки HTML тегов из текста сообщения
    const stripHtmlTags = useCallback((html) => {
        if (!html) return '';
        return html.replace(/<[^>]*>/g, '');
    }, []);

    // Функция для обработки нового сообщения
    const handleNewMessage = useCallback((messageData) => {
        // console.log('New message received:', messageData);

        // Проверяем, является ли сообщение новым (по времени или ID)
        const now = Date.now();
        const messageId = messageData.message_id || messageData.id;
        const isDuplicate = messageId === lastMessageIdRef.current ||
            (now - lastNotificationTimeRef.current < 1000);

        if (!isDuplicate && !messageData.isAuthor && !messageData.is_read) {
            lastNotificationTimeRef.current = now;
            lastMessageIdRef.current = messageId;

            // Воспроизводим звук
            playNotificationSound();

            // Создаем иконку пользователя из информации об авторе
            const userIcon = createUserIconFromAuthor(messageData.author);

            // Формируем текст для уведомления
            let notificationTitle = 'Новое сообщение';
            let notificationBody = '';

            // Добавляем информацию об авторе
            if (messageData.author && messageData.author.name) {
                notificationBody += `${messageData.author.name}\n`;
            } else if (messageData.author_name) {
                // Альтернативное поле для имени автора
                notificationBody += `${messageData.author_name}\n`;
            } else {
                notificationBody += 'Анонимный пользователь\n';
            }

            // Всегда выводим текст сообщения (даже если он пустой)
            // Проверяем разные возможные поля для текста
            const messageText = messageData.text || messageData.content || messageData.message || '';

            if (messageText) {
                const cleanText = stripHtmlTags(messageText);
                if (cleanText) {
                    notificationBody += cleanText.length > 100
                        ? cleanText.substring(0, 100) + '...'
                        : cleanText;
                } else {
                    notificationBody += 'Сообщение';
                }
            } else {
                notificationBody += 'Сообщение';
            }

            // Если есть файлы
            if (messageData.files && messageData.files.length > 0) {
                if (notificationBody) notificationBody += '\n';
                const fileCount = messageData.files.length;
                const fileWord = fileCount === 1 ? 'файл' :
                    (fileCount > 1 && fileCount < 5) ? 'файла' : 'файлов';
                notificationBody += `📎 ${fileCount} ${fileWord}`;
            }

            // Показываем браузерное уведомление с иконкой пользователя
            showNotification(notificationTitle, {
                body: notificationBody,
                tag: `message-${messageId}`,
                userIcon: userIcon, // Передаем созданную иконку пользователя
                data: {
                    messageId: messageId,
                    chatId: messageData.chat_id || messageData.chatId,
                    author: messageData.author?.name || messageData.author_name || 'Неизвестный'
                }
            });

            // console.log('Notification shown for message:', messageId);
        } else {
            // console.log('Skipping notification for message:', messageId, 'Reason:', {
            //     isDuplicate,
            //     isAuthor: messageData.isAuthor || messageData.is_author,
            //     isRead: messageData.is_read,
            //     self: messageData.self
            // });
        }
    }, [playNotificationSound, showNotification, stripHtmlTags, createUserIconFromAuthor]);

    // Получение токена для подключения WS
    const fetchGetToken = useCallback(() => {
        if (!isMountedRef.current || isTokenLoading) return;

        // Проверяем наличие валидного selectedSpace
        if (!selectedSpace || !selectedSpace.value) {
            return;
        }

        setIsTokenLoading(true);

        // Формируем заголовки только если есть валидный space
        const headers = {
            ...customHeaders,
            ...getSpace(selectedSpace)
        };

        const url = 'https://apps0.ecomru.ru:4454/ws/auth';

        fetch(url, {
            method: 'POST',
            headers
        })
            .then(res => {
                if (res.ok) {
                    return res.json();
                } else {
                    throw new Error('Bad response');
                }
            })
            .then(json => {
                // console.log('Token received:', json);
                if (json && json.key && isMountedRef.current) {
                    setTokenAuth(json.key);
                }
            })
            .catch(err => {
                // console.error('Token fetch error:', err);
                if (isMountedRef.current) {
                    setError('Ошибка получения токена');
                    // Повторная попытка через 5 секунд только если есть валидный space
                    setTimeout(() => {
                        if (selectedSpace && selectedSpace.value) {
                            fetchGetToken();
                        }
                    }, 5000);
                }
            })
            .finally(() => {
                if (isMountedRef.current) {
                    setIsTokenLoading(false);
                }
            });
    }, [isTokenLoading, selectedSpace]);

    // Проверка, существует ли уже активное WebSocket соединение
    const isWebSocketAlreadyOpen = useCallback(() => {
        return wsRef.current &&
            (wsRef.current.readyState === WebSocket.OPEN ||
                wsRef.current.readyState === WebSocket.CONNECTING);
    }, []);

    // Подключение к WebSocket
    const connectWebSocket = useCallback((token) => {
        if (!isMountedRef.current) return;

        // Проверяем, не открыто ли уже соединение
        if (isWebSocketAlreadyOpen()) {
            // console.log('WebSocket уже подключен или подключается, пропускаем создание нового соединения');
            return;
        }

        if (isConnecting) {
            // console.log('Уже идет процесс подключения...');
            return;
        }

        // Помечаем, что попытка подключения была
        isConnectionAttemptedRef.current = true;

        setIsConnecting(true);
        setError(null);

        const wsUrl = `wss://apps0.ecomru.ru:4454/ws/alerts?authorization=${token}&space_id=${selectedSpace?.value}`;
        const ws = new WebSocket(wsUrl);

        ws.onopen = () => {
            // console.log('WebSocket connected successfully');
            if (isMountedRef.current) {
                setIsConnecting(false);
                setIsConnected(true);
                setError(null);
                wsRef.current = ws;

                // Запрашиваем разрешение на уведомления после успешного подключения
                // ИСПРАВЛЕНО: проверяем существование Notification
                if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                    setTimeout(() => {
                        requestNotificationPermission();
                    }, 1000);
                }
            }
        };

        ws.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                // console.log('Raw WebSocket message:', message);
                // console.log('Message type:', typeof message);

                if (typeof message === 'object' && message !== null) {
                    // console.log('Message keys:', Object.keys(message));
                }

                if (!isMountedRef.current) return;

                // Проверяем разные форматы сообщений

                // Обработка сообщений типа "online" - обновление статуса пользователей
                if (message.type === 'online' && message.active_users !== undefined) {
                    // console.log('Received online status update:', message.active_users);
                    // Обновляем статус пользователей в глобальном состоянии
                    if (typeof setUserStatus === 'function') {
                        setUserStatus(message.active_users);
                        // console.log('User status updated in global store');
                    } else {
                        // console.error('setUserStatus is not a function');
                    }
                }

                // Проверяем разные форматы сообщений
                else if (message.unread_message_count !== undefined) {
                    // console.log('Received unread count update:', message.unread_message_count);

                    if (message?.chats) {
                        setChatListNotification(message?.chats)
                    }

                    // Обновляем счетчик непрочитанных сообщений
                    setUnreadCount(message.unread_message_count);
                    setUnreadCountPersonal(message.unread_private);
                    setUnreadCountGroup(message.unread_group);

                    // НЕ показываем уведомление о счетчике - только обновляем состояние
                    // Все уведомления будут показаны через handleNewMessage для конкретных сообщений
                }

                // Если пришло конкретное сообщение (формат из примера)
                else if (message.message_id && message.text !== undefined) {
                    handleNewMessage(message);
                }

                // Если пришло сообщение в другом формате (с data)
                else if (message.type === 'new_message' && message.data) {
                    handleNewMessage(message.data);
                }

                // Если приходит массив сообщений
                else if (Array.isArray(message)) {
                    // Обрабатываем только последнее сообщение (самое новое)
                    if (message.length > 0) {
                        const latestMessage = message[message.length - 1];
                        handleNewMessage(latestMessage);
                    }
                }

                // Обработка сообщений с content вместо text
                else if (message.content && message.message_id) {
                    // Преобразуем формат для совместимости с handleNewMessage
                    const formattedMessage = {
                        ...message,
                        text: message.content
                    };
                    handleNewMessage(formattedMessage);
                }

                // Обработка сообщений с другими полями
                else if (message.chat_message && message.message_id) {
                    handleNewMessage(message.chat_message);
                }

                // Обработка любого сообщения с message_id
                else if (message.message_id || message.id) {
                    handleNewMessage(message);
                }

                else {
                    // console.log('Unknown message format:', message);
                }
            } catch (err) {
                // console.error('Error parsing WebSocket message:', err);
            }
        };

        ws.onerror = (error) => {
            // console.error('WebSocket error:', error);
            if (isMountedRef.current) {
                setError('Ошибка подключения к серверу уведомлений');
                setIsConnecting(false);
            }
        };

        ws.onclose = (event) => {
            // console.log('WebSocket disconnected:', event.code, event.reason);

            if (isMountedRef.current) {
                setIsConnected(false);
                setIsConnecting(false);
                wsRef.current = null;

                // Сбрасываем токен, чтобы получить новый при переподключении
                setTokenAuth(null);

                // Автоматическое переподключение через 3 секунды
                // Только если соединение закрылось не по нашей инициативе (не код 1000)
                if (event.code !== 1000 && !reconnectTimeoutRef.current && isMountedRef.current) {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        if (isMountedRef.current) {
                            reconnectTimeoutRef.current = null;
                            // console.log('Reconnecting after disconnect...');
                            fetchGetToken(); // Получаем новый токен
                        }
                    }, 3000);
                }
            }
        };

        wsRef.current = ws;
        return ws;
    }, [fetchGetToken, playNotificationSound, isWebSocketAlreadyOpen, isConnecting, selectedSpace?.value, requestNotificationPermission, handleNewMessage, setChatListNotification, showNotification]);

    // Закрытие WebSocket соединения при размонтировании
    const disconnectWebSocket = useCallback(() => {
        if (wsRef.current) {
            // Закрываем сокет с кодом 1000 (нормальное закрытие)
            wsRef.current.close(1000, 'Component unmount');
            wsRef.current = null;
        }

        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, []);

    // Эффект для обработки смены space
    useEffect(() => {
        // Если space изменился
        if (prevSpaceRef.current?.value !== selectedSpace?.value) {
            // console.log('Space changed from', prevSpaceRef.current?.value, 'to', selectedSpace?.value);

            // Закрываем текущее соединение
            disconnectWebSocket();

            // Сбрасываем состояние
            setTokenAuth(null);
            setIsConnected(false);
            setIsConnecting(false);
            setUnreadCount(0);
            lastNotificationTimeRef.current = 0;
            lastMessageIdRef.current = null;

            // Получаем новый токен для нового space
            if (isMountedRef.current) {
                fetchGetToken();
            }

            // Обновляем ref с текущим space
            prevSpaceRef.current = selectedSpace;
        }
    }, [selectedSpace, disconnectWebSocket, fetchGetToken]);

    // Эффект для получения токена при монтировании
    useEffect(() => {
        isMountedRef.current = true;

        // Получаем токен при первом рендеринге только если еще не пытались подключиться
        if (!isConnectionAttemptedRef.current) {
            fetchGetToken();
        }

        return () => {
            isMountedRef.current = false;
            disconnectWebSocket();
        };
    }, []);

    // Эффект для подключения WebSocket при получении токена
    useEffect(() => {
        if (tokenAuth && !isConnected && !isConnecting && isMountedRef.current) {
            // console.log('Connecting WebSocket with new token...');

            // Проверяем, не открыто ли уже соединение перед созданием нового
            if (!isWebSocketAlreadyOpen()) {
                connectWebSocket(tokenAuth);
            } else {
                // console.log('WebSocket уже активен, пропускаем создание нового соединения');
                // Если сокет уже открыт, устанавливаем статус подключенного
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                    setIsConnected(true);
                }
            }
        }
    }, [tokenAuth, connectWebSocket, isWebSocketAlreadyOpen]);

    // Обновляем глобальное состояние при изменении unreadCount
    useEffect(() => {
        // Проверяем, что функция существует перед вызовом
        if (typeof setUnreadCountCommon === 'function') {
            setUnreadCountCommon(unreadCount);
        } else {
            // console.error('setUnreadCountCommon is not a function');
        }
    }, [unreadCount, setUnreadCountCommon]);

    return (
        <ButtonWithIcon
            icon={chat_big_icon}
            label={'Чат'}
            onClick={(e) => {
                setIsModalChat(false)
                window.scrollTo(0, 0)
                navigate('/chat')
            }}
            count={unreadCount}
        />
    );
};

export { NotificationChat };